/**
 * Creazione nuovo preventivo di VENDITA (Quote per cliente).
 *
 * Flusso minimo:
 * 1. Seleziona cliente (filtro su soggetti.tipo='cliente')
 * 2. Compila condizioni di vendita (validità, modalità pagamento, IVA default,
 *    sconto globale, destinatari extra per CC consulente)
 * 3. Salva bozza → redirect a dettaglio per aggiungere righe
 *
 * Le righe si aggiungono dal dettaglio (riusa endpoint POST /preventivi/{id}/righe).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Combobox, type ComboboxOption, Input, Label } from '@floraltech/ui';
import { useUO } from '@shell/UOContext';
import { useAuth } from '@shell/AuthContext';
import { SoggettoQuickCreateDialog, type SoggettoCreato } from '../components/SoggettoQuickCreateDialog';

interface Cliente {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  pec: string | null;
  telefono: string | null;
  partita_iva: string | null;
  codice_fiscale: string | null;
  codice_sdi: string | null;
}

interface ModalitaPagamentoOpt {
  id: string;
  codice: string;
  descrizione: string;
}

function clienteLabel(c: Cliente): string {
  return (
    c.ragione_sociale ||
    [c.nome, c.cognome].filter(Boolean).join(' ') ||
    '(senza nome)'
  );
}

export function NuovoPreventivoVendita() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { currentUO } = useUO();
  const { hasPermesso } = useAuth();

  // Picker cliente con ricerca SERVER-SIDE su /api/v1/soggetti (parametro
  // `q`, limite basso, debounce), stesso pattern di DuplicaPreventivoDialog.tsx:
  // prima scaricava fino a 500 clienti con `?limit=500` e filtrava in
  // memoria, con un tetto silenzioso oltre il quale i clienti in eccesso
  // restavano invisibili nel picker senza alcun errore (difetto noto,
  // Task 8c cantiere lista preventivi 2026-07-20).
  const [clienteResults, setClienteResults] = useState<Cliente[]>([]);
  const [clienteSearchLoading, setClienteSearchLoading] = useState(false);
  const [clienteSearchError, setClienteSearchError] = useState<string | null>(null);
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSelezionato, setClienteSelezionato] = useState<Cliente | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  // Avviso non bloccante mostrato quando il cliente creato al volo e' stato
  // selezionato con dati parziali (fetch di dettaglio fallito, vedi
  // handleClienteCreato): distinto da clienteSearchError, che sparirebbe
  // dalla UI appena clienteSelezionato viene valorizzato (review finale
  // punto 3).
  const [clienteCreatoAvviso, setClienteCreatoAvviso] = useState<string | null>(null);

  // Identifica la ricerca cliente corrente: incrementata prima di ogni
  // fetch, per scartare risposte tardive di query precedenti (sorpasso)
  // senza toccare risultati/caricamento della ricerca in corso. Stesso
  // pattern adottato in DuplicaPreventivoDialog.tsx.
  const clienteRicercaGenerazioneRef = useRef(0);

  const [modalitaOpts, setModalitaOpts] = useState<ModalitaPagamentoOpt[]>([]);
  const [aliquoteOpts, setAliquoteOpts] = useState<{ codice: string; descrizione: string; percentuale: number }[]>([]);

  // Tipo offerta: dettagliato (default), a_corpo, misto
  const [tipoOfferta, setTipoOfferta] = useState<'dettagliato' | 'a_corpo' | 'misto'>('dettagliato');

  // Condizioni vendita
  const [dataValidita, setDataValidita] = useState('');
  const [modalitaPagamento, setModalitaPagamento] = useState('');
  const [ivaDefault, setIvaDefault] = useState('22');
  const [scontoGlobale, setScontoGlobale] = useState('');
  const [note, setNote] = useState('');

  // Sezioni contrattuali (a_corpo / misto)
  const [oggetto, setOggetto] = useState('');
  const [premessa, setPremessa] = useState('');
  const [riferimentoProgetto, setRiferimentoProgetto] = useState('');
  const [importoACorpo, setImportoACorpo] = useState('');
  const [condizioniFornitura, setCondizioniFornitura] = useState(
    'Validita dell\'offerta. 90 (novanta) giorni dalla data del presente preventivo.\nTracciabilita dei flussi finanziari. Si garantisce il rispetto degli obblighi di tracciabilita dei flussi finanziari ai sensi della L. 136/2010 e s.m.i.',
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchModalita = useCallback(async () => {
    const res = await fetch('/api/v1/config/modalita-pagamento?solo_attive=true', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setModalitaOpts(Array.isArray(data) ? data : []);
    }
  }, [token]);

  const fetchAliquote = useCallback(async () => {
    const res = await fetch('/api/v1/classificazioni/aliquote-iva', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setAliquoteOpts(Array.isArray(data) ? data : []);
    }
  }, [token]);

  // Validita' rapida: imposta la data fine a oggi + n giorni (modificabile a calendario).
  const setValiditaGiorni = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    setDataValidita(d.toISOString().slice(0, 10));
  };

  useEffect(() => {
    fetchModalita();
    fetchAliquote();
    // Default sensato: validita' a 30 giorni, sempre modificabile.
    setValiditaGiorni(30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchModalita, fetchAliquote]);

  // Ricerca cliente debounced e server-side (mai in-memory, vedi commento
  // sopra la dichiarazione di clienteResults).
  useEffect(() => {
    if (clienteSelezionato) return;
    const timer = setTimeout(async () => {
      if (!clienteSearch.trim()) {
        setClienteResults([]);
        setClienteSearchError(null);
        return;
      }
      clienteRicercaGenerazioneRef.current += 1;
      const generazione = clienteRicercaGenerazioneRef.current;
      setClienteSearchLoading(true);
      setClienteSearchError(null);
      try {
        const params = new URLSearchParams({ tipo: 'cliente', q: clienteSearch.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Una ricerca piu' recente e' gia' partita: questa risposta e'
        // superata, va ignorata senza toccare risultati ne' errore.
        if (clienteRicercaGenerazioneRef.current !== generazione) return;
        if (res.ok) {
          const data = await res.json();
          if (clienteRicercaGenerazioneRef.current !== generazione) return;
          setClienteResults(Array.isArray(data) ? data : data.items ?? []);
        } else {
          setClienteResults([]);
          setClienteSearchError(`Ricerca cliente fallita (${res.status})`);
        }
      } catch {
        // Errore di rete: se nel frattempo e' partita una ricerca piu'
        // recente il sorpasso e' gia' gestito sopra, quindi qui e' un
        // errore reale della ricerca corrente.
        if (clienteRicercaGenerazioneRef.current === generazione) {
          setClienteResults([]);
          setClienteSearchError('Errore di rete durante la ricerca: verifica la connessione e riprova.');
        }
      } finally {
        if (clienteRicercaGenerazioneRef.current === generazione) setClienteSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [clienteSearch, clienteSelezionato, token]);

  /**
   * Cliente creato al volo dal picker: il POST di creazione (gestito da
   * SoggettoQuickCreateDialog) restituisce gia' il soggetto completo, ma il
   * tipo `SoggettoCreato` esposto e' volutamente stretto (manca PEC/C.F./SDI
   * mostrati nella card di riepilogo sotto): un fetch mirato per id recupera
   * i campi restanti senza scaricare l'intera anagrafica.
   *
   * Se quel fetch fallisce (rete, permessi, backend riavviato nel mezzo) NON
   * si azzera piu' la selezione: il cliente e' comunque stato creato
   * (`creato.id` e' valido), quindi si ripiega sui dati gia' restituiti
   * dalla creazione. Prima lasciava clienteSelezionato a null senza alcun
   * messaggio: bottone Salva disabilitato e nessuna spiegazione del perche'
   * (review finale, punto 3).
   */
  const handleClienteCreato = useCallback(
    async (creato: SoggettoCreato) => {
      setClienteCreatoAvviso(null);
      try {
        const res = await fetch(`/api/v1/soggetti/${creato.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setClienteSelezionato(await res.json());
        } else {
          setClienteSelezionato({
            ...creato,
            telefono: null,
            codice_fiscale: null,
            codice_sdi: null,
            pec: null,
          });
          setClienteCreatoAvviso(
            `Cliente creato, ma alcuni dati (PEC, SDI, codice fiscale, telefono) non sono stati caricati (errore ${res.status}). Sono disponibili dalla scheda cliente.`,
          );
        }
      } catch {
        setClienteSelezionato({
          ...creato,
          telefono: null,
          codice_fiscale: null,
          codice_sdi: null,
          pec: null,
        });
        setClienteCreatoAvviso(
          "Cliente creato, ma il recupero dei dati completi e' fallito per un errore di rete. Sono disponibili dalla scheda cliente.",
        );
      }
      setClienteSearch('');
      setClienteResults([]);
      setClienteSearchError(null);
    },
    [token],
  );

  const salva = async () => {
    if (!clienteSelezionato) {
      setError('Seleziona un cliente prima di salvare.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const body: Record<string, unknown> = {
        cliente_id: clienteSelezionato.id,
        tipo_offerta: tipoOfferta,
      };
      if (currentUO?.id) body.unita_organizzativa_id = currentUO.id;
      if (dataValidita) body.data_validita = dataValidita;
      if (note) body.note = note;

      if (tipoOfferta === 'dettagliato' || tipoOfferta === 'misto') {
        if (modalitaPagamento) body.modalita_pagamento = modalitaPagamento;
        if (ivaDefault) body.iva_default = parseFloat(ivaDefault);
        if (scontoGlobale) body.sconto_globale_perc = parseFloat(scontoGlobale);
      }

      if (premessa.trim()) body.premessa = premessa.trim();
      if (condizioniFornitura.trim()) body.condizioni_fornitura = condizioniFornitura.trim();

      if (tipoOfferta === 'a_corpo' || tipoOfferta === 'misto') {
        if (oggetto.trim()) body.oggetto = oggetto.trim();
        if (riferimentoProgetto.trim()) body.riferimento_progetto = riferimentoProgetto.trim();
        if (importoACorpo) body.importo_a_corpo = parseFloat(importoACorpo);
        if (modalitaPagamento) body.modalita_pagamento = modalitaPagamento;
      }

      const res = await fetch('/api/v1/preventivi/vendita', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Errore ${res.status}`);
      }
      const created = await res.json();
      navigate(`/vendite/preventivi/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Nuovo preventivo vendita</h1>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Annulla
        </Button>
      </div>

      {error && (
        <div className="rounded-md border p-3" style={{ borderColor: 'var(--color-destructive)', color: 'var(--color-destructive)' }}>
          {error}
        </div>
      )}

      <Card className="p-4 space-y-3">
        <h2 className="text-lg font-semibold">1. Cliente</h2>

        {clienteSelezionato ? (
          <div className="rounded-md border p-3 flex items-start justify-between gap-3" style={{ background: 'var(--color-bg-subtle, #F4F6FB)' }}>
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold">{clienteLabel(clienteSelezionato)}</div>
              {clienteSelezionato.partita_iva && <div className="text-xs">P.IVA: {clienteSelezionato.partita_iva}</div>}
              {clienteSelezionato.codice_fiscale && clienteSelezionato.codice_fiscale !== clienteSelezionato.partita_iva && (
                <div className="text-xs">C.F.: {clienteSelezionato.codice_fiscale}</div>
              )}
              {clienteSelezionato.email && <div className="text-xs">Email: {clienteSelezionato.email}</div>}
              {clienteSelezionato.pec && <div className="text-xs">PEC: {clienteSelezionato.pec}</div>}
              {clienteSelezionato.codice_sdi && <div className="text-xs">SDI: {clienteSelezionato.codice_sdi}</div>}
              {clienteCreatoAvviso && (
                <div className="text-xs mt-1 rounded border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300 px-2 py-1">
                  {clienteCreatoAvviso}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setClienteSelezionato(null); setClienteSearch(''); setClienteResults([]); setClienteSearchError(null); setClienteCreatoAvviso(null); }}
            >
              Cambia
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            <Label>Cliente *</Label>
            <Combobox
              value={clienteSearch}
              onInputChange={setClienteSearch}
              onSelect={(opt) => {
                const c = clienteResults.find((x) => x.id === opt.value);
                if (c) setClienteSelezionato(c);
              }}
              options={clienteResults.map<ComboboxOption>((c) => ({
                value: c.id,
                label: clienteLabel(c),
                sublabel: [
                  c.partita_iva ? `P.IVA ${c.partita_iva}` : '',
                  c.codice_fiscale && c.codice_fiscale !== c.partita_iva ? `C.F. ${c.codice_fiscale}` : '',
                  c.email || '',
                ].filter(Boolean).join(' - '),
              }))}
              loading={clienteSearchLoading}
              placeholder="Cerca cliente per ragione sociale, nome o P.IVA..."
              onCreateNew={hasPermesso('fornitori.modifica') ? () => setQuickCreateOpen(true) : undefined}
              createNewLabel="Crea nuovo cliente..."
            />
            {clienteSearchError && (
              <div className="text-xs" style={{ color: 'var(--color-destructive)' }}>
                {clienteSearchError}
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4 space-y-3">
        <h2 className="text-lg font-semibold">2. Tipo offerta</h2>
        <div className="grid grid-cols-3 gap-3">
          {([
            { v: 'a_corpo', titolo: 'A corpo', desc: 'Prezzo forfettario unico. Voci solo titolo+descrizione. Per servizi, consulenze, progetti.' },
            { v: 'dettagliato', titolo: 'Dettagliato', desc: 'Righe con quantita, prezzo unitario, IVA, sconto. Per vendita prodotti.' },
            { v: 'misto', titolo: 'Misto', desc: 'Sezione a corpo + righe dettagliate (servizi + materiali).' },
          ] as const).map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setTipoOfferta(opt.v as 'dettagliato' | 'a_corpo' | 'misto')}
              className="text-left rounded-md border p-3"
              style={{
                borderColor: tipoOfferta === opt.v ? 'var(--color-primary, #1B3A6B)' : 'var(--color-border, #e5e7eb)',
                background: tipoOfferta === opt.v ? 'var(--color-bg-subtle, #F4F6FB)' : 'transparent',
              }}
            >
              <div className="font-semibold text-sm">{opt.titolo}</div>
              <div className="text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
                {opt.desc}
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h2 className="text-lg font-semibold">3. Condizioni</h2>
        {currentUO && (
          <div className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            Ramo emittente: <strong>{currentUO.nome}</strong> (selezionato dal menu in alto)
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label>Validità (data fine)</Label>
            <Input
              type="date"
              value={dataValidita}
              onChange={(e) => setDataValidita(e.target.value)}
            />
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setValiditaGiorni(15)}
                className="rounded border px-2 py-1 text-xs"
                style={{ borderColor: 'var(--color-border)' }}
              >
                +15 gg
              </button>
              <button
                type="button"
                onClick={() => setValiditaGiorni(30)}
                className="rounded border px-2 py-1 text-xs"
                style={{ borderColor: 'var(--color-border)' }}
              >
                +30 gg
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Modalità di pagamento</Label>
            <select
              className="w-full border rounded-md p-2"
              value={modalitaPagamento}
              onChange={(e) => setModalitaPagamento(e.target.value)}
            >
              <option value="">(nessuna)</option>
              {modalitaOpts.map((m) => (
                <option key={m.id} value={m.descrizione}>{m.descrizione}</option>
              ))}
            </select>
          </div>
        </div>

        {(tipoOfferta === 'dettagliato' || tipoOfferta === 'misto') && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>IVA default</Label>
              <select
                className="w-full border rounded-md p-2"
                value={ivaDefault}
                onChange={(e) => setIvaDefault(e.target.value)}
              >
                {aliquoteOpts.length === 0 && <option value={ivaDefault}>{ivaDefault}%</option>}
                {aliquoteOpts.length > 0 && !aliquoteOpts.some((a) => String(a.percentuale) === ivaDefault) && (
                  <option value={ivaDefault}>{ivaDefault}%</option>
                )}
                {aliquoteOpts.map((a) => (
                  <option key={a.codice} value={String(a.percentuale)}>
                    {a.percentuale}% - {a.descrizione}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Sconto globale (%)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="opzionale"
                value={scontoGlobale}
                onChange={(e) => setScontoGlobale(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="space-y-3 pt-2 border-t">
          {(tipoOfferta === 'a_corpo' || tipoOfferta === 'misto') && (
            <>
              <div className="space-y-1">
                <Label>Oggetto</Label>
                <textarea
                  className="w-full border rounded-md p-2 min-h-16 text-sm"
                  value={oggetto}
                  onChange={(e) => setOggetto(e.target.value)}
                  placeholder="es. Preventivo per servizi di comunicazione e divulgazione nell'ambito del progetto..."
                  maxLength={2000}
                />
              </div>
              <div className="space-y-1">
                <Label>Riferimento progetto (opzionale)</Label>
                <Input
                  value={riferimentoProgetto}
                  onChange={(e) => setRiferimentoProgetto(e.target.value)}
                  placeholder="es. PR Calabria FESR-FSE 2021-2027, RSO 2.6, Azione 2.6.1"
                  maxLength={500}
                />
              </div>
            </>
          )}
          <div className="space-y-1">
            <Label>Premessa</Label>
            <textarea
              className="w-full border rounded-md p-2 min-h-20 text-sm"
              value={premessa}
              onChange={(e) => setPremessa(e.target.value)}
              placeholder="es. Con la presente, in riscontro alla richiesta di preventivo formulata..."
              maxLength={5000}
            />
          </div>
          {(tipoOfferta === 'a_corpo' || tipoOfferta === 'misto') && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Importo a corpo (totale forfettario)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={importoACorpo}
                  onChange={(e) => setImportoACorpo(e.target.value)}
                  placeholder="es. 33500.00"
                />
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label>Condizioni di fornitura e note/clausole</Label>
            <textarea
              className="w-full border rounded-md p-2 min-h-24 text-sm"
              value={condizioniFornitura}
              onChange={(e) => setCondizioniFornitura(e.target.value)}
              placeholder="Validita dell'offerta..."
              maxLength={5000}
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label>Note interne</Label>
          <textarea
            className="w-full border rounded-md p-2 min-h-20"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Annotazioni interne (non appaiono nel PDF)"
          />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={salva} disabled={loading || !clienteSelezionato}>
          {loading ? 'Salvataggio…' : 'Crea bozza e procedi'}
        </Button>
      </div>

      <SoggettoQuickCreateDialog
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        tipo="cliente"
        presetNome={clienteSearch}
        onCreated={handleClienteCreato}
      />
    </div>
  );
}
